import { useState } from 'react';
import { FiTrash2 } from 'react-icons/fi';
import { BsBuilding } from 'react-icons/bs';
import { formatPhone } from '../../../../utlis';
import MyTable from '../../../../components/MyTable';

// Map API account status to display text
const mapAccountStatus = (status) => {
  if (!status) return 'N/A';
  const statusLower = status.toLowerCase();
  const displayMapping = {
    'pending': 'Pending',
    'active': 'Active',
    'closed': 'Closed',
    'deleted': 'Deleted',
  };
  return displayMapping[statusLower] || status;
};

const AccountTable = ({ data, onEditClient, onDeleteClient, onManageCompanies, pageSize, onPageSizeChange }) => {
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  // Transform data to ensure consistent field names
  const transformedData = data.map((client) => ({
    id: client.record_id || client.client_id || client.id || '',
    client_code: client.client_code || '',
    business_name: client.business_name || '',
    email: client.email || '',
    phone: client.phone || '',
    first_name: client.first_name || '',
    last_name: client.last_name || '',
    full_name: (client.first_name && client.last_name)
      ? `${client.first_name} ${client.last_name}`.trim()
      : client.first_name || client.last_name || '',
    city: client.city || '',
    state: client.state || '',
    account_status: client.account_status || '',
    total_licenses: client.total_licenses ?? '',
    active_licenses: client.active_licenses ?? '',
    inactive_licenses: client.inactive_users ?? '',
  }));

  const columns = [
    {
      name: 'id',
      label: 'ID',
      options: {
        display: false,
      },
    },
    {
      name: 'business_name',
      label: 'Business Name',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const id = tableMeta.rowData[0];
          return (
            <h3
              onClick={() => onEditClient(id)}
              className="text-base font-bold text-auxiliary-800 cursor-pointer hover:underline"
            >
              {value || 'N/A'}
            </h3>
          );
        },
      },
    },
    {
      name: 'client_code',
      label: 'Client Code',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (
          <p className="text-text-hint">{value || 'N/A'}</p>
        ),
      },
    },
    {
      name: 'email',
      label: 'Email ID',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value || 'N/A',
      },
    },
    {
      name: 'phone',
      label: 'Phone Number',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => formatPhone(value) || 'N/A',
      },
    },
    {
      name: 'full_name',
      label: 'Contact Name',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value || 'N/A',
      },
    },
    {
      name: 'city',
      label: 'City',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value || 'N/A',
      },
    },
    {
      name: 'state',
      label: 'State',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value || 'N/A',
      },
    },
    {
      name: 'total_licenses',
      label: 'Total Licenses',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (value !== '' && value !== null && value !== undefined ? value : 'N/A'),
      },
    },
    {
      name: 'active_licenses',
      label: 'Active Licenses',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (value !== '' && value !== null && value !== undefined ? value : 'N/A'),
      },
    },
    {
      name: 'inactive_licenses',
      label: 'Inactive Licenses',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (value !== '' && value !== null && value !== undefined ? value : 'N/A'),
      },
    },
    {
      name: 'account_status',
      label: 'Status',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (
          <p className="text-text-default font-bold">{mapAccountStatus(value)}</p>
        ),
      },
    },
    {
      name: 'actions',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_value, tableMeta) => {
          const clientId = tableMeta.rowData[0];
          return (
            <div className="flex items-center gap-1">
              <button
                onClick={() => onManageCompanies(clientId)}
                className="p-2 hover:bg-secondary-100 rounded-full"
                title="Manage Companies"
              >
                <BsBuilding size={16} className="text-secondary-700" />
              </button>
              <button
                onClick={() => setDeleteTargetId(clientId)}
                className="p-2 hover:bg-red-100 rounded-full"
                title="Delete Client"
              >
                <FiTrash2 size={16} className="text-red-500" />
              </button>
            </div>
          );
        },
      },
    },
  ];

  const options = {
    filterType: 'dropdown',
    responsive: 'standard',
    rowsPerPage: pageSize,
    rowsPerPageOptions: [5, 10, 15, 20, 50],
    onChangeRowsPerPage: (numberOfRows) => onPageSizeChange(numberOfRows),
    selectableRows: 'none',
    elevation: 0,
    download: true,
    print: true,
    search: false,
    viewColumns: true,
    filter: true,
  };

  return (
    <div className="w-full overflow-x">
      <MyTable
        title=""
        data={transformedData}
        columns={columns}
        options={options}
      />

      {/* Delete Confirmation Dialog */}
      {deleteTargetId && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-sm flex flex-col items-center gap-4">
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-red-100 mb-2">
              <FiTrash2 size={28} className="text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 text-center">Delete Account?</h3>
            <p className="text-sm text-gray-500 text-center">
              Are you sure you want to delete this account? This action cannot be undone.
            </p>
            <div className="flex gap-3 w-full mt-2">
              <button
                onClick={() => setDeleteTargetId(null)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onDeleteClient(deleteTargetId);
                  setDeleteTargetId(null);
                }}
                className="flex-1 py-2 px-4 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountTable;
